import { BaseReactiveClass } from 'Classes/BaseReactiveClass';
import { FarmClass } from 'Classes/FarmClass';
import { MarketOfferClass } from 'Classes/MarketOfferClass';
import { INVESTMENT_STATUS, SM_STATUS, TRANSFER_OWNERSHIP_STATUS } from 'Const/investment.json';
import { get, upperFirst } from 'Utils/common';
import { httpClientInvestment } from 'Api/microservice-http-clients';
import { getUrlParamsByObject } from 'Utils/url';
import { ModelAccount } from 'Components/Account/models';
import CONFIG from 'Config';

export class InvestmentClass extends BaseReactiveClass {
  constructor(props) {
    super(props);

    // this is set on get list of exited investment
    this.isExited = this.isExited || false;
  }

  get farm() {
    return this._farm;
  }

  set farm(farmData) {
    this.$set('_farm', new FarmClass(farmData));
  }

  get profile() {
    return this._profile;
  }

  set profile(profileData) {
    this.$set('_profile', new ModelAccount(profileData));
  }

  // eslint-disable-next-line camelcase
  get sm_status() {
    return this._sm_status;
  }

  get isSmStatusUndefined() {
    return this._sm_status === undefined;
  }

  // eslint-disable-next-line camelcase
  set sm_status(value) {
    this.$set('_sm_status', value);
  }

  // eslint-disable-next-line camelcase
  get sm_offer() {
    return this._sm_offer;
  }

  // eslint-disable-next-line camelcase
  set sm_offer(offerData) {
    this.$set('_sm_offer', new MarketOfferClass({ ...offerData, id: this.sm_offer_id }));
  }

  get isSMStatusEligible() {
    return this.sm_status === SM_STATUS.ELIGIBLE;
  }

  get isSMStatusActive() {
    return this.sm_status === SM_STATUS.ACTIVE;
  }

  get isSMStatusSalePending() {
    return this.sm_status === SM_STATUS.SALE_PENDING;
  }

  get isSMStatusSold() {
    return this.sm_status === SM_STATUS.SOLD;
  }

  get isSMStatusCancelled() {
    return this.sm_status === SM_STATUS.CANCELLED;
  }

  get isCanCreateMarketOffer() {
    return this.isEligibleInvestment || this.isSMStatusCancelled;
  }

  get isCanCancelMarketOffer() {
    return this.isSMStatusActive;
  }

  get isHasOffer() {
    return Boolean(this.sm_offer_id) && !this.isSMStatusCancelled;
  }

  get isTransferByMarket() {
    return this.reason_for_transfer === 'sold_on_sm';
  }

  get isTransferByTransferOwnership() {
    return this.reason_for_transfer === 'direct';
  }

  get isHoldPeriodEnd() {
    const days90ms = 7776E6; // 90 days in MS
    const confirmDate = new Date(this.confirm_date);
    const endDate = new Date(confirmDate.getTime() + days90ms);
    return endDate < Date.now();
  }

  get isSuccessfullyClosed() {
    return this.status === INVESTMENT_STATUS.SUCCESSFULLY_CLOSED;
  }

  get isCancellationRequested() {
    return this.status === INVESTMENT_STATUS.CANCELED_AFTER_INVESTMENT_BY_INVESTOR;
  }

  get isStatusEscrowFailed() {
    return this.status === INVESTMENT_STATUS.ESCROW_FAILED;
  }

  get isStatusSold() {
    return this.status === INVESTMENT_STATUS.SOLD;
  }

  get isStatusExited() {
    return this.status === INVESTMENT_STATUS.EXITED;
  }

  get isAccreditationDeclined() {
    return this.status === INVESTMENT_STATUS.ACCREDITATION_DECLINED;
  }

  get isEligibleInvestment() {
    return this.isSMStatusEligible && (!this.isTransferOwnership
      || this.profile?.isTypeIndividual);
  }

  get ownershipTypeTitle() {
    const { type } = this.profile;
    if (this.profile?.isTypeSdira) return 'SDIRA';
    if (this.profile?.isTypeSolo) return 'Solo 401k';
    return upperFirst(type || '');
  }

  get isFundingTypeAcreTraderWallet() {
    const { type } = this.funding || {};
    return type === 'balance';
  }

  get isFundingTypeBankAccount() {
    const { type } = this.funding || {};
    return type === 'bank';
  }

  get fundingTypeTitle() {
    const { type } = this.funding || {};
    if (!type && this.isTransferOwnership && this.isActiveTab) return 'N/A';
    if (this.isExited && this.isEscrowTypeAch) return 'ACH';
    if (this.isFundingTypeAcreTraderWallet) return 'AcreTrader Wallet';
    if (this.isEscrowTypeWire) return 'WIRE';
    if (this.isFundingTypeBankAccount) return 'Bank Account';
    if (this.profile?.isTypeSdira) return 'SDIRA';
    return type;
  }

  get isActiveTab() {
    return this.isSuccessfullyClosed && !this.isExited;
  }

  get isEscrowTypeWire() {
    return this.funding?.type === 'WIRE';
  }

  get custodianWebsite() {
    if (!this.profile?.isTypeSdira) return '';
    if (this.profile?.isCustodianAlto) return CONFIG.urls.altoAuth;
    return get(this.profile, 'ownership.custodian_website', '');
  }

  get isEscrowTypeAch() {
    return this.funding?.type === 'ACH';
  }

  get escrowTransactionStatus() {
    return this.funding?.transaction?.status || '';
  }

  get isCanWire() {
    return this.isEscrowTypeWire && !this.isSuccessfullyClosed && !this.isCancellationRequested;
  }

  get isWalletEscrow() {
    return this.funding?.funding_source.length > 7;
  }

  get isCanReInitiateTransaction() {
    return this.isStatusEscrowFailed && !this.isEscrowTypeWire && !this.isEscrowTypeAch;
  }

  get isFarmSubscribedFull() {
    return this.farm?.farmSubscribedPercent === 100;
  }

  get isCanCancelInvestment() {
    return [
      INVESTMENT_STATUS.CONFIRMATION,
      INVESTMENT_STATUS.COMPLETED,
      INVESTMENT_STATUS.CONFIRMATION_PENDING,
      INVESTMENT_STATUS.ACCREDITATION_DECLINED,
      INVESTMENT_STATUS.ESCROW_FAILED,
      INVESTMENT_STATUS.ACCREDITATION_DOC_SENT,
    ].includes(this.status) && !this.isFarmSubscribedFull;
  }

  get taxDocuments() {
    return this.tax_documents || [];
  }

  get isTaxDocument() {
    return Boolean(this.taxDocuments.length);
  }

  get isNewTaxDocument() {
    return this.taxDocuments.some(doc => !doc.read);
  }

  get isNewDocument() {
    return this.farm.isNewDocument;
  }

  get isNewFinancialStatementDocument() {
    return this.farm.isNewFinancialStatementDocument;
  }

  get pricePerShareLast() {
    return this.amount / this.number_of_shares;
  }

  get isStatusTransferOwnership() {
    return [TRANSFER_OWNERSHIP_STATUS.INITIATED, TRANSFER_OWNERSHIP_STATUS.SIGNED].includes(this.transfer_ownership_status);
  }

  get isStatusTransferOwnershipInitiated() {
    return this.transfer_ownership_status === TRANSFER_OWNERSHIP_STATUS.INITIATED;
  }

  get isStatusTransferOwnershipSigned() {
    return this.transfer_ownership_status === TRANSFER_OWNERSHIP_STATUS.SIGNED;
  }

  get isTransferOwnership() {
    return this.isStatusTransferOwnership || this.isTransferByTransferOwnership;
  }

  get confirmDateText() {
    if (this.isTransferByTransferOwnership && this.isActiveTab) return 'Transferred Date:';
    return 'Submitted Date:';
  }

  async getDocusignUrlForExitedInvestment(docName) {
    if (!docName || !this.isExited) return null;
    const { data } = await httpClientInvestment.get(`/exited/${this.former_owner_id}/signed/${docName}`);
    return data?.url;
  }

  async getDocusignUrl(docName, id, resign = false, ssn) {
    if (!docName || !id) return null;
    const urlParams = getUrlParamsByObject({ resign, ssn: ssn || undefined });
    const { data } = await httpClientInvestment.get(`${id}/signing/${docName}${urlParams}`);
    return data?.url;
  }

  async getTransferOwnershipUrl(id, resign = false) {
    if (!this.transfer_ownership_id || !id) return null;
    const urlParams = getUrlParamsByObject({ resign });
    const { data } = await httpClientInvestment.get(`${id}/transfer-ownership/${this.transfer_ownership_id}/signing/joinder-agreement${urlParams}`);
    return data?.url;
  }

  escrow() {
    return this.funding; // legacy
  }
}
